/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    CalendarDays,
    Database,
    Monitor,
    TriangleAlert,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/* Import components */
import StatusModal from '@/components/feedback/StatusModal';
import DemoSensorDeviceFilterControls from '@/components/tabs/demo/DemoSensorDeviceFilterControls';

/* Import configuration */
import { HAS_NEUTRAUBLING_SENSOR_DATA } from '@/config/csvSensors';

/* Import API functions */
import { eeCheckApi } from '@/api/eeCheckApi';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { MonitoringUrl } from '@/lib/sensors/urls/monitoringUrl';
import { MuensterTimeSeriesUrl } from '@/lib/sensors/muenster/muensterTimeSeriesUrl';
import { getSensorDeviceDescription } from '@/lib/sensors/presentation/sensorDeviceDescription';
import {
    createDemoDeviceSensorCatalog,
    getDemoSensorTargets,
    getDemoVirtualDevices,
    type SensorDeviceCustomer,
    type SensorGroupFilter,
} from '@/lib/sensors/demo/demoDeviceSensors';
import {
    DEMO_SENSOR_SUPPLY_TYPE_OPTIONS,
    type DemoSensorDeviceAvailability,
    type DemoSensorDeviceGroupBy,
} from '@/lib/sensors/demo/demoSensorDeviceSelection';

interface SensorDeviceRow {
    dataUrl: string | null;
    deviceDescription: string | null;
    group: string;
    sensorName: string | null;
    sensorType: string;
    sensorUrl: string | null;
}

const IS_MUENSTER_DATA_PROXY_ENABLED = import.meta.env.DEV;

function createSensorRows(
    deviceSensor: DeviceSensor,
    deviceSensorCatalog: DeviceSensorCatalog,
    muensterDate: string,
    sensorGroupFilter: SensorGroupFilter,
): SensorDeviceRow[] {
    return getDemoSensorTargets(deviceSensor, deviceSensorCatalog, sensorGroupFilter).map((target) => ({
        dataUrl: target.group === '-' && target.sensorType === '-' && target.sensorName
            ? new MuensterTimeSeriesUrl(target.sensorName).getUrl(muensterDate)
            : null,
        deviceDescription: deviceSensor.getDeviceDescription(),
        group: target.group,
        sensorName: target.sensorName,
        sensorType: target.sensorType,
        sensorUrl: new MonitoringUrl(target.sensorName).getUrl(),
    }));
}

function SensorsDevicesTab() {
    const { t } = useI18n();
    const [selectedCustomer, setSelectedCustomer] = useState<SensorDeviceCustomer>(
        HAS_NEUTRAUBLING_SENSOR_DATA ? 'neutraubling' : 'muenster',
    );
    const deviceSensorCatalog = useMemo(() => {
        return createDemoDeviceSensorCatalog(selectedCustomer);
    }, [selectedCustomer]);
    const [selectedAvailability, setSelectedAvailability] = useState<DemoSensorDeviceAvailability>('enabled');
    const [selectedVirtualDeviceGroupBy, setSelectedVirtualDeviceGroupBy] = useState<DemoSensorDeviceGroupBy>('system');
    const [selectedSensorGroupFilter, setSelectedSensorGroupFilter] = useState<SensorGroupFilter>('ALL');
    const [selectedMuensterDate, setSelectedMuensterDate] = useState('2025-01-01');
    const [muensterDataModal, setMuensterDataModal] = useState({
        externalUrl: null as string | null,
        isOpen: false,
        responseText: '',
        title: '',
    });
    const [muensterDataRequestError, setMuensterDataRequestError] = useState<string | null>(null);
    const virtualDevices = useMemo(() => {
        return getDemoVirtualDevices(
            deviceSensorCatalog,
            selectedSensorGroupFilter,
            selectedAvailability === 'enabled',
        );
    }, [deviceSensorCatalog, selectedAvailability, selectedSensorGroupFilter]);
    const [selectedVirtualDevice, setSelectedVirtualDevice] = useState<string>(virtualDevices[0] ?? '');

    useEffect(() => {
        if (!virtualDevices.length) {
            setSelectedVirtualDevice('');
            return;
        }

        if (!virtualDevices.includes(selectedVirtualDevice)) {
            setSelectedVirtualDevice(virtualDevices[0] ?? '');
        }
    }, [selectedVirtualDevice, virtualDevices]);

    const selectedDeviceSensor = useMemo(() => {
        return new DeviceSensor(selectedVirtualDevice, deviceSensorCatalog);
    }, [deviceSensorCatalog, selectedVirtualDevice]);
    const selectedDeviceMetadata = useMemo(() => {
        return deviceSensorCatalog.getSensorMetadata(selectedVirtualDevice);
    }, [deviceSensorCatalog, selectedVirtualDevice]);
    const sensorRows = useMemo(() => {
        if (!selectedVirtualDevice) {
            return [];
        }

        return createSensorRows(
            selectedDeviceSensor,
            deviceSensorCatalog,
            selectedMuensterDate,
            selectedSensorGroupFilter,
        );
    }, [deviceSensorCatalog, selectedDeviceSensor, selectedMuensterDate, selectedSensorGroupFilter]);

    async function handleMuensterDataRequest(url: string, sensorName: string | null) {
        const title = sensorName ?? t('Münster data');

        if (!IS_MUENSTER_DATA_PROXY_ENABLED) {
            setMuensterDataModal({
                externalUrl: url,
                isOpen: true,
                responseText: '',
                title,
            });
            return;
        }

        try {
            const { data } = await eeCheckApi.requestMuensterTimeSeries(url);

            setMuensterDataModal({
                externalUrl: null,
                isOpen: true,
                responseText: data,
                title,
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error.');

            setMuensterDataRequestError(t('Unable to load Münster data: {message}', { message }));
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Sensors / Devices')}</h2>
                <p>{t('Select a sensor/device to inspect all available sensor names and links.')}</p>
            </section>

            <DemoSensorDeviceFilterControls
                availability={selectedAvailability}
                customer={selectedCustomer}
                deviceSensorCatalog={deviceSensorCatalog}
                extraControl={selectedCustomer === 'muenster' ? (
                    <>
                        <label
                            className="tooltip tooltip-top inline-flex items-center gap-1 text-sm font-semibold"
                            htmlFor="muenster-data-date-input"
                            data-tip={t('Date used for the Münster data link.')}
                        >
                            <CalendarDays className="h-4 w-4" aria-hidden="true" />
                            {t('Date')}
                        </label>
                        <input
                            id="muenster-data-date-input"
                            type="date"
                            className="input input-bordered w-auto"
                            value={selectedMuensterDate}
                            onChange={(event) => {
                                setSelectedMuensterDate(event.target.value);
                            }}
                        />
                    </>
                ) : undefined}
                groupBy={selectedVirtualDeviceGroupBy}
                groupFilter={selectedSensorGroupFilter}
                onAvailabilityChange={setSelectedAvailability}
                onCustomerChange={setSelectedCustomer}
                onDeviceChange={setSelectedVirtualDevice}
                onGroupByChange={setSelectedVirtualDeviceGroupBy}
                onGroupFilterChange={setSelectedSensorGroupFilter}
                selectedDevice={selectedVirtualDevice}
                virtualDevices={virtualDevices}
            />

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-5">
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Name')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedDeviceMetadata?.name ?? 'N/A'}
                        </dd>
                    </div>
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Address')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedDeviceMetadata?.address ?? 'N/A'}
                        </dd>
                    </div>
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Description')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedDeviceMetadata?.description ?? 'N/A'}
                        </dd>
                    </div>
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Category')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedDeviceSensor.getDeviceCategoryLabel() ?? 'N/A'}
                        </dd>
                    </div>
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Enabled')}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-base-content">
                            {selectedDeviceMetadata
                                ? selectedDeviceMetadata.enabled ? t('Sensor enabled') : t('Sensor not enabled')
                                : 'N/A'}
                        </dd>
                    </div>
                </dl>
            </section>

            {selectedDeviceMetadata && !selectedDeviceMetadata.enabled ? (
                <section className="alert alert-warning" role="alert">
                    <TriangleAlert className="h-5 w-5" aria-hidden="true" />
                    <span>{t('Attention: This is a disabled sensor. Some sensor time series in the following table may not be available.')}</span>
                </section>
            ) : null}

            <section>
                {sensorRows.length > 0 ? (
                    <div className="overflow-x-auto rounded-box border border-base-content/20">
                        <table className="table table-sm w-full">
                            <thead className="bg-base-content/10 text-sm text-base-content/80">
                                <tr>
                                    <th className="px-4 py-3">{t('Group / supply type')}</th>
                                    <th className="px-4 py-3">{t('Sensor type')}</th>
                                    <th className="px-4 py-3">{t('Sensor name')}</th>
                                    <th className="px-4 py-3">{t('Links')}</th>
                                    <th className="px-4 py-3">{t('Description')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-base-300">
                                {sensorRows.map((row) => {
                                    const description = getSensorDeviceDescription(row.group, row.sensorType);

                                    return (
                                        <tr
                                            key={`${row.group}-${row.sensorType}-${row.sensorName}`}
                                            className="even:bg-base-content/5 hover:bg-base-content/10"
                                        >
                                            <td className="align-top px-4 py-3">
                                                {t(
                                                    DEMO_SENSOR_SUPPLY_TYPE_OPTIONS.find(({ value }) => value === row.group)?.label
                                                    ?? row.group,
                                                )}
                                            </td>
                                            <td className="align-top px-4 py-3">{row.sensorType}</td>
                                            <td className="align-top break-all px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                                {row.sensorName ?? '-'}
                                            </td>
                                            <td className="align-top px-4 py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {row.sensorUrl ? (
                                                        <a
                                                            href={row.sensorUrl}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="btn btn-ghost btn-xs tooltip tooltip-top"
                                                            aria-label={t('Link to the monitoring server')}
                                                            data-tip={t('Link to the monitoring server')}
                                                        >
                                                            <Monitor className="h-4 w-4" aria-hidden="true" />
                                                        </a>
                                                    ) : null}
                                                    {row.dataUrl ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs tooltip tooltip-top"
                                                            aria-label={t('Link to the Münster data request for date {date}', { date: selectedMuensterDate })}
                                                            data-tip={t('Link to the Münster data request for date {date}', { date: selectedMuensterDate })}
                                                            onClick={() => {
                                                                void handleMuensterDataRequest(row.dataUrl!, row.sensorName);
                                                            }}
                                                        >
                                                            <Database className="h-4 w-4" aria-hidden="true" />
                                                        </button>
                                                    ) : null}
                                                    {!row.sensorUrl && !row.dataUrl ? '-' : null}
                                                </div>
                                            </td>
                                            <td className="min-w-64 align-top px-4 py-3 whitespace-pre-wrap">
                                                {description ? t(description) : row.deviceDescription ?? '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="rounded-box border border-dashed border-base-300 px-4 py-8 text-center text-sm text-base-content/70">
                        {selectedVirtualDevice
                            ? t('No sensors were found for the current selection.')
                            : t('No sensor/device is available for the current selection.')}
                    </div>
                )}
            </section>

            {muensterDataModal.isOpen ? (
                <div
                    className="modal modal-open"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="muenster-data-modal-title"
                >
                    <div className="modal-box max-w-4xl">
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <h2 id="muenster-data-modal-title">{t('Münster data')}</h2>
                                <p className="mt-1 truncate font-mono text-sm text-base-content/70">
                                    {muensterDataModal.title}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                aria-label={t('Close')}
                                onClick={() => {
                                    setMuensterDataModal((state) => ({ ...state, isOpen: false }));
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {muensterDataModal.externalUrl ? (
                            <a
                                href={muensterDataModal.externalUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="link link-primary block break-all rounded-box border border-base-300 bg-base-200/60 p-4 font-mono text-xs"
                            >
                                {muensterDataModal.externalUrl}
                            </a>
                        ) : (
                            <textarea
                                readOnly
                                className="textarea textarea-bordered min-h-80 w-full resize-y font-mono text-xs"
                                aria-label={t('Raw response')}
                                value={muensterDataModal.responseText}
                            />
                        )}

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setMuensterDataModal((state) => ({ ...state, isOpen: false }));
                                }}
                            >
                                {t('OK')}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop bg-black/40"
                        onClick={() => {
                            setMuensterDataModal((state) => ({ ...state, isOpen: false }));
                        }}
                    />
                </div>
            ) : null}

            <StatusModal
                isOpen={muensterDataRequestError !== null}
                message={muensterDataRequestError ?? ''}
                onClose={() => {
                    setMuensterDataRequestError(null);
                }}
                title={t('Münster data')}
            />
        </div>
    );
}

export default SensorsDevicesTab;
